import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type {
  FindingDecisionEvent,
  FindingDemoUpdate,
  FindingItem,
} from "@/features/findings/types";

interface FindingDemoOverride {
  status?: string;
  updatedAt?: string;
  body?: Record<string, unknown>;
  linkedEvidence?: Record<string, unknown>[];
  history?: FindingDecisionEvent[];
}

interface FindingDemoOverridesContextValue {
  applyUpdate: (findingId: string, update: FindingDemoUpdate) => void;
  createFinding: (finding: FindingItem) => void;
  linkConversationToFinding: (
    conversationId: string,
    findingId: string,
    evidence?: Record<string, unknown>,
  ) => void;
  getConversationFindingId: (
    conversationId: string,
    fallbackFindingId?: string,
  ) => string | undefined;
  findLocalFinding: (findingId: string) => FindingItem | undefined;
  createdFindings: readonly FindingItem[];
  withOverride: (finding: FindingItem) => FindingItem;
}

const FindingDemoOverridesContext = createContext<
  FindingDemoOverridesContextValue | undefined
>(undefined);

const FINDING_BODY_UPDATE_FIELDS = [
  ["severity", "severity"],
  ["owner", "owner"],
  ["resolutionNote", "resolutionNote"],
  ["remediationStatus", "remediationStatus"],
  ["remediationPlanStatus", "remediationPlanStatus"],
  ["remediationState", "remediationState"],
  ["remediationOwner", "remediationOwner"],
  ["remediationDueDate", "remediationDueDate"],
  ["remediationPlan", "remediationPlan"],
  ["remediationTicket", "remediationTicket"],
  ["remediationTasks", "remediationTasks"],
  ["verificationCriteria", "verificationCriteria"],
  ["verificationStatus", "verificationStatus"],
  ["verificationHistory", "verificationHistory"],
  ["closureReview", "closureReview"],
  ["decision", "decision"],
  ["incident", "incident"],
  ["investigation", "investigation"],
] as const satisfies ReadonlyArray<readonly [keyof FindingDemoUpdate, string]>;

export function FindingDemoOverridesProvider({ children }: { children: ReactNode }) {
  const [findingOverrides, setFindingOverrides] = useState<
    Record<string, FindingDemoOverride>
  >({});
  const [createdFindings, setCreatedFindings] = useState<FindingItem[]>([]);
  const [conversationFindingLinks, setConversationFindingLinks] = useState<
    Record<string, string>
  >({});

  const applyUpdate = useCallback((findingId: string, update: FindingDemoUpdate) => {
    const updatedAt = new Date().toISOString();
    const nextOverride: FindingDemoOverride = {
      status: update.status,
      updatedAt,
      body: getFindingBodyUpdate(update),
    };

    setFindingOverrides((current) => ({
      ...current,
      [findingId]: {
        ...current[findingId],
        ...nextOverride,
        body: { ...current[findingId]?.body, ...nextOverride.body },
        history: update.historyEvent
          ? [
              ...(current[findingId]?.history ?? []),
              { ...update.historyEvent, timestamp: updatedAt },
            ]
          : current[findingId]?.history,
      },
    }));
  }, []);

  const withOverride = useCallback(
    (finding: FindingItem): FindingItem =>
      mergeFindingWithOverride(finding, findingOverrides[finding.id]),
    [findingOverrides],
  );

  const createFinding = useCallback((finding: FindingItem) => {
    setCreatedFindings((current) =>
      current.some((item) => item.id === finding.id) ? current : [...current, finding],
    );
  }, []);

  const linkConversationToFinding = useCallback(
    (conversationId: string, findingId: string, evidence?: Record<string, unknown>) => {
      setConversationFindingLinks((current) => ({
        ...current,
        [conversationId]: findingId,
      }));

      if (!evidence) return;
      setFindingOverrides((current) => {
        const existing = current[findingId];
        const linkedEvidence = existing?.linkedEvidence ?? [];
        const alreadyLinked = linkedEvidence.some(
          (item) => item.label === evidence.label && item.source === evidence.source,
        );

        return {
          ...current,
          [findingId]: {
            ...existing,
            linkedEvidence: alreadyLinked
              ? linkedEvidence
              : [...linkedEvidence, evidence],
          },
        };
      });
    },
    [],
  );

  const getConversationFindingId = useCallback(
    (conversationId: string, fallbackFindingId?: string) =>
      conversationFindingLinks[conversationId] ?? fallbackFindingId,
    [conversationFindingLinks],
  );

  const findLocalFinding = useCallback(
    (findingId: string) => createdFindings.find((finding) => finding.id === findingId),
    [createdFindings],
  );

  const value = useMemo(
    () => ({
      applyUpdate,
      createFinding,
      linkConversationToFinding,
      getConversationFindingId,
      findLocalFinding,
      createdFindings,
      withOverride,
    }),
    [
      applyUpdate,
      createFinding,
      linkConversationToFinding,
      getConversationFindingId,
      findLocalFinding,
      createdFindings,
      withOverride,
    ],
  );

  return createElement(FindingDemoOverridesContext.Provider, { value }, children);
}

export function useFindingDemoOverrides(): FindingDemoOverridesContextValue {
  const context = useContext(FindingDemoOverridesContext);
  if (!context) {
    throw new Error(
      "useFindingDemoOverrides must be used within FindingDemoOverridesProvider",
    );
  }

  return context;
}

function mergeFindingWithOverride(
  finding: FindingItem,
  override?: FindingDemoOverride,
): FindingItem {
  if (!override) return finding;
  return {
    ...finding,
    status: override.status ?? finding.status,
    updatedAt: override.updatedAt ?? finding.updatedAt,
    body: {
      ...finding.body,
      ...override.body,
      ...(override.linkedEvidence?.length
        ? {
            evidence: mergeEvidence(
              Array.isArray(finding.body.evidence) ? finding.body.evidence : [],
              override.linkedEvidence,
            ),
          }
        : {}),
      ...(override.history?.length
        ? {
            decisionHistory: [
              ...(Array.isArray(finding.body.decisionHistory)
                ? finding.body.decisionHistory
                : []),
              ...override.history,
            ],
          }
        : {}),
    },
  };
}

function mergeEvidence(
  baseEvidence: unknown[],
  linkedEvidence: readonly Record<string, unknown>[],
) {
  const result = [...baseEvidence];
  for (const evidence of linkedEvidence) {
    const alreadyPresent = result.some(
      (item) =>
        typeof item === "object" &&
        item !== null &&
        !Array.isArray(item) &&
        (item as Record<string, unknown>).label === evidence.label &&
        (item as Record<string, unknown>).source === evidence.source,
    );
    if (!alreadyPresent) result.push(evidence);
  }
  return result;
}

function getFindingBodyUpdate(update: FindingDemoUpdate): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const [source, target] of FINDING_BODY_UPDATE_FIELDS) {
    const value = update[source];
    if (value !== undefined) body[target] = value;
  }
  return body;
}
